import { useTranslation } from "react-i18next";
import { Empty, message } from 'antd';
import { useCallback, useRef } from 'react';
import { DISLIKE_REACTION, LIKE_REACTION } from '../../../../../../../constants';
import './CommentFeed.scss';
import CommentSection, { MenuOption } from './CommentSection';
import { CommentNode } from './commentTree';
import { RawComment } from './commentTypes';
import { useAuthStore } from '../../../../../../../stores/stores';

export interface Discussion {
	id: number;
	rootComment: RawComment;
	subComments: RawComment[];
	isSave?: boolean;
	isPinned?: boolean;
}

interface CommentFeedProps {
	data: Discussion[];
	getMenuOptions?: (comment: CommentNode) => MenuOption[];
	onEdit?: (id: number, content: string, attachments?: any) => Promise<RawComment>;
	onDelete?: (node: CommentNode) => Promise<void>;
	onSubmitReply?: (data: {
		discussionId: number;
		parentId: number;
		replyToId: number;
		level: number;
		content: string;
		attachments?: File[];
	}) => Promise<RawComment>;
	onReact?: (id: number, reactionType: 'like' | 'dislike') => Promise<void>;
	onDataUpdate?: (updatedData: Discussion[]) => void;
	className?: string;
	onSeenDiscuss: (id: number) => Promise<void>;

}

export default function CommentFeed({
	data,
	getMenuOptions,
	onEdit,
	onDelete,
	onSubmitReply,
	onReact,
	onDataUpdate,
	className,
	onSeenDiscuss
}: CommentFeedProps) {
  const { t } = useTranslation();
	const newReplyIdsRef = useRef<Set<number>>(new Set());
	const userInfo = useAuthStore((state) => state.user);

	const updateDiscussions = useCallback((updater: (discussions: Discussion[]) => Discussion[]) => {
		const newData = updater([...data]);
		onDataUpdate?.(newData);
	}, [data, onDataUpdate]);

	const clearNewReplyIds = useCallback((nodeId: number) => {
		data.forEach(discussion => {
			if (discussion.rootComment.id === nodeId) {
				discussion.subComments.forEach(sub => {
					if (sub.parentId === nodeId) {
						newReplyIdsRef.current.delete(sub.id);
					}
				});
			}

			discussion.subComments.forEach(sub => {
				if (sub.id === nodeId) {
					discussion.subComments
						.filter(s => s.parentId === nodeId)
						.forEach(s => newReplyIdsRef.current.delete(s.id));
				}
			});
		});
	}, [data]);

	const handleSubmitReply = useCallback(async (replyData: {
		discussionId: number;
		parentId: number;
		replyToId: number;
		level: number;
		content: string;
		attachments?: File[];
	}) => {
		if (!onSubmitReply) throw new Error('onSubmitReply handler not provided');

		try {
			const newComment = await onSubmitReply(replyData);
			newReplyIdsRef.current.add(newComment.id);

			updateDiscussions(discussions => {
				const discussionIndex = discussions.findIndex(d => d.id === replyData.discussionId);
				if (discussionIndex === -1) return discussions;

				const discussion = discussions[discussionIndex];
				const processedComment: RawComment = {
					...newComment,
					discussionId: replyData.discussionId,
					avatar: newComment.avatar || userInfo?.anhDaiDien || `${process.env.PUBLIC_URL}/images/comment-avatar.png`,
					attachments: processAttachments(newComment.attachments, newComment.id),
					reactionCount: newComment.reactionCount || []
				};

				let updatedRootComment = discussion.rootComment;
				if (replyData.parentId === discussion.rootComment.id) {
					updatedRootComment = {
						...discussion.rootComment,
						totalReply: (discussion.rootComment.totalReply || 0) + 1
					};
				}

				const updatedSubComments = [...discussion.subComments];
				if (replyData.parentId !== discussion.rootComment.id) {
					const parentIndex = updatedSubComments.findIndex(c => c.id === replyData.parentId);
					if (parentIndex !== -1) {
						updatedSubComments[parentIndex] = {
							...updatedSubComments[parentIndex],
							totalReply: (updatedSubComments[parentIndex].totalReply || 0) + 1
						};
					}
				}

				updatedSubComments.push(processedComment);

				const updatedDiscussions = [...discussions];
				updatedDiscussions[discussionIndex] = {
					...discussion,
					rootComment: updatedRootComment,
					subComments: updatedSubComments
				};

				return updatedDiscussions;
			});

		} catch (error) {
			throw error;
		}
	}, [onSubmitReply, updateDiscussions]);

	const handleEdit = useCallback(async (id: number, content: string, attachments?: any) => {
		if (!onEdit) throw new Error('onEdit handler not provided');

		try {
			const updated = await onEdit(id, content, attachments);

			updateDiscussions(discussions => {
				return discussions.map(discussion => {
					if (discussion.rootComment.id === id) {
						return {
							...discussion,
							rootComment: {
								...discussion.rootComment,
								...updated,
								attachments: mergeAttachments(
									discussion.rootComment.attachments || [],
									updated.attachments || [],
									attachments?.removedAttachmentIds || []
								),
								reactionCount: updated.reactionCount || discussion.rootComment.reactionCount || [],
								avatar: updated.avatar || discussion.rootComment.avatar
							}
						};
					}

					const updatedSubComments = discussion.subComments.map(comment => {
						if (comment.id === id) {
							return {
								...comment,
								...updated,
								attachments: mergeAttachments(
									comment.attachments || [],
									updated.attachments || [],
									attachments?.removedAttachmentIds || []
								),
								reactionCount: updated.reactionCount || comment.reactionCount || [],
								avatar: updated.avatar || comment.avatar
							};
						}
						return comment;
					});

					return {
						...discussion,
						subComments: updatedSubComments
					};
				});
			});
		} catch (error) {
			throw error;
		}
	}, [onEdit, updateDiscussions]);

	const handleDelete = useCallback(async (node: CommentNode) => {
		if (!onDelete) throw new Error('onDelete handler not provided');

		try {
			await onDelete(node);

			updateDiscussions(discussions => {
				const discussionToDelete = discussions.find(d => d.rootComment.id === node.id);
				if (discussionToDelete) {
					return discussions.filter(d => d.rootComment.id !== node.id);
				}

				return discussions.map(discussion => {
					const commentToDelete = discussion.subComments.find(c => c.id === node.id);
					if (!commentToDelete) return discussion;

					const filteredSubComments = discussion.subComments.filter(c => c.id !== node.id);

					let updatedRootComment = discussion.rootComment;
					let updatedSubComments = filteredSubComments;

					if (commentToDelete.parentId) {
						if (commentToDelete.parentId === discussion.rootComment.id) {
							updatedRootComment = {
								...discussion.rootComment,
								totalReply: Math.max(0, (discussion.rootComment.totalReply || 0) - 1)
							};
						} else {
							updatedSubComments = filteredSubComments.map(comment => {
								if (comment.id === commentToDelete.parentId) {
									return {
										...comment,
										totalReply: Math.max(0, (comment.totalReply || 0) - 1)
									};
								}
								return comment;
							});
						}
					}

					return {
						...discussion,
						rootComment: updatedRootComment,
						subComments: updatedSubComments
					};
				});
			});

			newReplyIdsRef.current.delete(node.id);
		} catch (error) {
			throw error;
		}
	}, [onDelete, updateDiscussions]);

	const handleReact = useCallback(async (commentId: number, isLike: boolean) => {
		if (!onReact) throw new Error('React handler not provided');

		try {
			await onReact(commentId, isLike ? 'like' : 'dislike');

			updateDiscussions(discussions => {
				return discussions.map(discussion => {
					const updateCommentReaction = (comment: RawComment): RawComment => {
						if (comment.id !== commentId) return comment;

						const currentReactions = comment.reactionCount || [];
						const likeReaction = currentReactions.find(r => r.reactionType === LIKE_REACTION);
						const dislikeReaction = currentReactions.find(r => r.reactionType === DISLIKE_REACTION);

						let newLikeCount = likeReaction?.count || 0;
						let newDislikeCount = dislikeReaction?.count || 0;
						let newMyReaction = comment.myReaction;

						if (isLike) {
							if (comment.myReaction === LIKE_REACTION) {
								newMyReaction = null;
								newLikeCount = Math.max(0, newLikeCount - 1);
							} else if (comment.myReaction === DISLIKE_REACTION) {
								newMyReaction = LIKE_REACTION;
								newLikeCount += 1;
								newDislikeCount = Math.max(0, newDislikeCount - 1);
							} else {
								newMyReaction = LIKE_REACTION;
								newLikeCount += 1;
							}
						} else {
							if (comment.myReaction === DISLIKE_REACTION) {
								newMyReaction = null;
								newDislikeCount = Math.max(0, newDislikeCount - 1);
							} else if (comment.myReaction === LIKE_REACTION) {
								newMyReaction = DISLIKE_REACTION;
								newDislikeCount += 1;
								newLikeCount = Math.max(0, newLikeCount - 1);
							} else {
								newMyReaction = DISLIKE_REACTION;
								newDislikeCount += 1;
							}
						}

						return {
							...comment,
							myReaction: newMyReaction,
							reactionCount: [
								{ reactionType: LIKE_REACTION, count: newLikeCount },
								{ reactionType: DISLIKE_REACTION, count: newDislikeCount }
							]
						};
					};

					if (discussion.rootComment.id === commentId) {
						return {
							...discussion,
							rootComment: updateCommentReaction(discussion.rootComment)
						};
					}

					return {
						...discussion,
						subComments: discussion.subComments.map(updateCommentReaction)
					};
				});
			});
		} catch (error) {
			message.error(t("Không thể thực hiện thao tác", "Không thể thực hiện thao tác"));
			throw error;
		}
	}, [onReact, updateDiscussions]);

	if (!data || data.length === 0 || data === null) {
		return (
			<div className={`cmt-feed ${className || ''}`}>
				<div className="cmt-feed-empty">
					<Empty description={t("Không có bình luận", "Không có bình luận")}></Empty>
				</div>
			</div>
		);
	}

	else return (
		<div className={`cmt-feed ${className || ''}`}>
			{data.map(discussion => (
				<CommentSection
					key={discussion.id}
					discussionId={discussion.id}
					root={discussion.rootComment}
					replies={discussion.subComments}
					isPinned={discussion.isPinned}
					isSaved={discussion.isSave}
					menuOptions={getMenuOptions}
					onEdit={handleEdit}
					onDelete={handleDelete}
					onSubmitReply={handleSubmitReply}
					onReact={handleReact}
					newReplyIds={newReplyIdsRef.current}
					onNodeExpanded={clearNewReplyIds}
					onSeenDiscuss={onSeenDiscuss}

				/>
			))}
		</div>
	);
}

function processAttachments(attachments: any[] | undefined, commentId: number) {
	if (!attachments) return [];
	return attachments.map(att => ({
		...att,
		id: att.id || Date.now(),
		commentId: att.commentId || commentId,
		mainKey: att.mainKey.startsWith('http')
			? att.mainKey
			: `${process.env.REACT_APP_BASE_API_URL}/v2/open-api/cdn?path=${att.mainKey}`
	}));
}

function mergeAttachments(oldAttachments: any[], newAttachments: any[], removedIds: number[]) {
	const remainingOld = oldAttachments.filter(att => !removedIds.includes(att.id));
	const processedNew = processAttachments(newAttachments, 0);
	return [...remainingOld, ...processedNew];
}